import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { categoryColor, groupColor } from '../../lib/entityColors';
import { fetchExploreCategories } from '../../lib/exploreApi';
import type { ExploreCategoriesResponse, ExploreCategoryGroup } from '../../lib/exploreApi';

// Legacy intel categories (CVE / Techniques / Actors / Families) live outside
// the IOC registry — they have their own Neo4j node types and richer columns.
const INTEL_CATEGORIES = [
  { value: 'cve',       label: 'CVEs' },
  { value: 'technique', label: 'TTPs' },
  { value: 'actor',     label: 'Actors' },
  { value: 'family',    label: 'Families' },
  { value: 'country',   label: 'Countries' },
] as const;

interface ExploreHeaderProps {
  query: string;
  onQueryChange: (q: string) => void;
  category: string;
  onCategoryChange: (cat: string) => void;
  sortBy: string;
  onSortByChange: (sort: string) => void;
  detectedHint?: string;
}

/** Sum counts of all sub-categories in a group. */
function groupTotal(grp: ExploreCategoryGroup): number {
  return grp.categories.reduce((sum, c) => sum + c.count, 0);
}

/** Resolve which group is currently active based on the selected category key. */
function activeGroup(
  category: string,
  groups: ExploreCategoryGroup[],
): ExploreCategoryGroup | null {
  if (!category) return null;
  if (category.startsWith('group:')) {
    const key = category.slice('group:'.length);
    return groups.find(g => g.key === key) ?? null;
  }
  return groups.find(g => g.categories.some(c => c.key === category)) ?? null;
}

export default function ExploreHeader({
  query, onQueryChange,
  category, onCategoryChange,
  sortBy, onSortByChange,
  detectedHint,
}: ExploreHeaderProps) {
  const { data: catsData } = useQuery<ExploreCategoriesResponse>({
    queryKey: ['explore-categories'],
    queryFn: fetchExploreCategories,
    staleTime: 60_000,
  });

  const groups = catsData?.groups ?? [];

  const currentGroup = useMemo(
    () => activeGroup(category, groups),
    [category, groups],
  );

  const currentSubKey = !category.startsWith('group:') ? category : '';

  return (
    <div className="explore-header">
      <div className="explore-search-row">
        <div className="explore-search-input-wrap">
          <svg className="explore-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="7" cy="7" r="4.5" />
            <line x1="10.5" y1="10.5" x2="14" y2="14" />
          </svg>
          <input
            className="explore-search-input"
            type="text"
            placeholder="Search IOCs, CVEs, techniques, actors, families..."
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            autoFocus
          />
          {query && (
            <button className="explore-search-clear" onClick={() => onQueryChange('')} title="Clear">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <line x1="3" y1="3" x2="11" y2="11" />
                <line x1="11" y1="3" x2="3" y2="11" />
              </svg>
            </button>
          )}
        </div>

        {query && (
          <select
            className="explore-sort-select"
            value={sortBy}
            onChange={(e) => onSortByChange(e.target.value)}
          >
            <option value="doc_count">Most referenced</option>
            <option value="value">Alphabetical</option>
          </select>
        )}
      </div>

      {detectedHint && (
        <div className="explore-detected-hint">Detected: {detectedHint}</div>
      )}

      <div className="explore-tabs">
        <button
          className={`explore-tab${category === '' ? ' explore-tab--active' : ''}`}
          onClick={() => onCategoryChange('')}
        >
          All
        </button>

        {groups.length > 0 && (
          <span className="explore-tab-group-label">Indicators</span>
        )}
        {groups.map((grp) => {
          const groupKey = `group:${grp.key}`;
          const isActive = category === groupKey || currentGroup?.key === grp.key;
          const total = groupTotal(grp);
          return (
            <button
              key={grp.key}
              className={`explore-tab${isActive ? ' explore-tab--active' : ''}`}
              onClick={() => onCategoryChange(groupKey)}
              style={isActive ? { '--tab-color': groupColor(grp.key) } as React.CSSProperties : undefined}
              title={`${grp.label}: ${total.toLocaleString()} entries`}
            >
              {grp.label}
            </button>
          );
        })}

        {groups.length > 0 && <span className="explore-tab-group-sep" />}

        <span className="explore-tab-group-label">Intel</span>
        {INTEL_CATEGORIES.map(({ value, label }) => (
          <button
            key={value}
            className={`explore-tab${category === value ? ' explore-tab--active' : ''}`}
            onClick={() => onCategoryChange(value)}
            style={category === value ? { '--tab-color': categoryColor(value) } as React.CSSProperties : undefined}
          >
            {label}
          </button>
        ))}
      </div>

      {currentGroup && currentGroup.categories.length > 1 && (
        <div className="explore-subtype-chips">
          <button
            className={`explore-chip${category === `group:${currentGroup.key}` ? ' explore-chip--active' : ''}`}
            onClick={() => onCategoryChange(`group:${currentGroup.key}`)}
            style={{ '--tab-color': groupColor(currentGroup.key) } as React.CSSProperties}
          >
            All {currentGroup.label}
          </button>
          {currentGroup.categories.map((c) => {
            const isActive = currentSubKey === c.key;
            return (
              <button
                key={c.key}
                className={`explore-chip${isActive ? ' explore-chip--active' : ''}`}
                onClick={() => onCategoryChange(c.key)}
                style={{ '--tab-color': categoryColor(c.key) } as React.CSSProperties}
                title={`${c.label}: ${c.count.toLocaleString()} entries`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
